import { registerPlugin } from "@capacitor/core";
import {
  ActivityKey,
  HealthIntegrationKey,
  SubscriptionSource,
} from "@sworkit/api";
import { HealthLog } from "../health";

export type SyncFilesInBackgroundOptions = {
  paths: string[];
  mainDirectory: string;
  directory: string;
};
export type PlaySoundOptions = {
  url?: string;
  urls?: string[];
};
export type SetAudioPositionOptions = {
  currentTime: number;
};
export type AudioDuckingOptions = {
  duck: boolean;
};
export type LoadAudioOptions = {
  uri: string;
  imageUrl?: string;
  name?: string;
  seriesName?: string;
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onError?: (error: ErrorEvent) => void;
  onEnd?: () => void;
};

export type NativeAudioCallback = {
  onReady?: boolean;
  onPlay?: boolean;
  onPause?: boolean;
  onError?: string;
  onEnd?: boolean;
};
export type PreloadOptions = {
  paths: string[];
};
export type FirebaseLogEventOptions = {
  name: string;
  parameters: { [key: string]: any };
};
/**
 * @param totalFileCount The total number of files to be downloaded
 * @param sizeOfFilesDownloaded The total size of the file size downloaded so far
 * @param numberOfFilesDownloaded The total number of files downloaded
 */
export type DownloadProgress = {
  totalFileCount: number;
  sizeOfFilesDownloaded: number;
  numberOfFilesDownloaded: number;
};

export type RemoteUrl = {
  url: string;
};
export type FileVerificationResponse = {
  remoteUrl: string;
  localPath: string;
};
/**
 * remoteConfigKey is a string which represents the key set in Firebase Remote Config Experiment
 *
 */
export type FirebaseRemoteConfigKey = {
  remoteConfigKey: string;
  type: "string" | "boolean" | "double" | "long";
};
export declare type FirebaseRemoteConfigValue<T> = {
  remoteConfigValue: T;
};
export type NativeAdvertisingId = {
  adid: string;
  idfa?: string;
  idfv?: string;
  android_id?: string;
  isLimitAdTrackingEnabled: boolean;
};

export type NativeWorkoutLog = {
  timestampMillis: number;
  activityKey: ActivityKey;
  calories: number;
  minutesCompleted: number;
  deviceType: string;
  logType: "sworkout" | "apple-health" | "google-fit" | "health-connect";
  utcCreated: string;
  createdOn: string;
  platform: "android" | "ios";
  appVersion: string;
  externalUuid: string;
};

export type HealthLogSearch = {
  startTime: Date;
  endTime?: Date;
  limit?: number;
  integration: HealthIntegrationKey
};

export type FitLogSearch = {
  startTime: string;
  endTime?: string;
  limit?: number;
  integration: HealthIntegrationKey
};

export type StepLogSearch = {
  startTime?: string | Date;
  endTime?: string | Date;
};

export type SetAudioMutedOptions = {
  muted: boolean;
};

export interface SworkitMobilePlugin {
  setAudioMuted(options: SetAudioMutedOptions): Promise<any>;
  playBackgroundAudio(): Promise<any>;
  pauseBackgroundAudio(): Promise<any>;
  /**
   * release and reset any audio items
   */
  stopBackgroundAudio(): Promise<any>;
  setCurrentAudioPosition(options: SetAudioPositionOptions): Promise<any>;
  loginWithFacebook(): Promise<any>;
  loginWithGoogle(): Promise<any>;
  loginWithApple(): Promise<any>;
  //@todo add personify to swapi
  loginWithOIDC(options: {
    provider: SubscriptionSource | "personify-health" | "personify-pilot";
    scopes: string[];
  }): Promise<any>;

  syncFilesInBackground(
    options: SyncFilesInBackgroundOptions,
  ): Promise<string[]>;
  //**callback */
  loadAudioPlayer(
    options: LoadAudioOptions,
    callback: (success: NativeAudioCallback, error: any) => void,
  ): any;
  duckAudioToggle(options: AudioDuckingOptions): Promise<any>;
  playAudio(options: PlaySoundOptions): Promise<any>;
  pauseAudio(): Promise<any>;
  resumeAudio(): Promise<any>;
  getCurrentAudioDuration(): Promise<{ duration: number }>;
  getAudioVolume(): Promise<{ volume: number }>;
  setAudioVolume(options: {
    volume: number;
    externalAudioPlayer?: HTMLAudioElement;
  }): Promise<{ volume: number }>;
  preloadAudio(options: PreloadOptions): Promise<any>;
  preloadVideo(options: PreloadOptions): Promise<any>;
  preloadImage(options: PreloadOptions): Promise<any>;
  keepScreenOn(): Promise<any>; //remove...use capacitor community plugin instead
  letScreenSleep(): Promise<any>; //remove ..use capacitor community plugin instead
  logEvent(options: FirebaseLogEventOptions): Promise<any>;
  downloadSyncProgress(
    options: object,
    callback: (success: DownloadProgress, error: any) => void,
  ): any;
  findLocalFileFromUrl(options: RemoteUrl): Promise<FileVerificationResponse>;
  getRemoteConfigValueForKey<T>(
    options: FirebaseRemoteConfigKey,
  ): Promise<FirebaseRemoteConfigValue<T>>;
  /** Must resolve this promise before calling `getRemoteConfigValueForKey` */
  fetchAndActivateRemoteConfig(): Promise<void>;
  stopAudio(): Promise<any>;
  getAdvertisingId(): Promise<NativeAdvertisingId>;
  requestReview(): Promise<any>;
  /**
   * Sworkit reads workout logs and steps from Health and Fit
   * @param options
   */
  healthRead(
    options: HealthLogSearch | FitLogSearch,
  ): Promise<{ workoutLogs: NativeWorkoutLog[] }>;
  /**
   * Sworkit saves workout logs to Health and Fit
   * @param options
   */
  healthStore(options: HealthLog): Promise<any>;
  disconnectHealth(): Promise<{ success: boolean }>;
  // requestReadWorkoutPermissions(): Promise<{ success: boolean }>;
  // requestWriteWorkoutPermissions(): Promise<{ success: boolean }>;
  // requestAllPermissions(): Promise<{ success: boolean }>;
  requestHealthPermissions(options: {
    integration: HealthIntegrationKey;
  }): Promise<{ success: boolean }>;

  isHealthAvailable(options: {integration: HealthIntegrationKey}): Promise<{ available: boolean }>;
  logInToFirebaseWithCustomToken(token: {
    customToken: string;
  }): Promise<{ success: boolean }>;
  // requestReadStepDataPermissions(): Promise<{ success: boolean }>;
  signOutNativeFirebase(): Promise<void>;
  /**
   * Check for native Firebase log in, must send uid to match user
   */
  isLoggedInNativeFirebase(options: {
    uid: string;
  }): Promise<{ authorized: boolean }>;
  /**
   * iOS returns an error if HealthKit step type is not approved
   */
  readStepCount(options?: StepLogSearch): Promise<{ message: string }>;
  getAudioCurrentTime(): Promise<{ currentTime: number }>;
}
const SworkitMobile = registerPlugin<SworkitMobilePlugin>("SworkitMobile", {
  web: () => import("./sw-mobile-web").then((m) => new m.SworkitMobileWeb()),
});

export default SworkitMobile;
